/**
 * Light / dark appearance of the student app.
 *
 * `html[data-theme]` is the single source of truth: `applyDocumentTheme` is the only writer, the
 * inline bootstrap in `index.html` writes the same marker before any script loads (no white flash in
 * dark mode), SCSS reads it through the `--axl-*` tokens and antd's static APIs follow it (see
 * `staticAntTheme.tsx`). Keep this module free of antd/React — it is imported before React mounts.
 */
export type ThemeMode = "system" | "light" | "dark";
export type ResolvedTheme = Exclude<ThemeMode, "system">;

/** Also read by the inline bootstrap in `index.html` — rename both together. */
export const THEME_STORAGE_KEY = "school.theme-mode";

const themeModes: ThemeMode[] = ["system", "light", "dark"];

export const isThemeMode = (value: unknown): value is ThemeMode =>
  typeof value === "string" && themeModes.includes(value as ThemeMode);

/** What the student picked themselves; null = never picked (the school's default applies). */
export function readStoredThemeMode(): ThemeMode | null {
  try {
    const value = window.localStorage.getItem(THEME_STORAGE_KEY);
    return isThemeMode(value) ? value : null;
  } catch {
    // Private windows and embedded webviews may deny storage: the school's default then.
    return null;
  }
}

/**
 * The school's theme policy (portal design `theme`): its default mode and whether students may switch.
 * Cached in localStorage so the inline bootstrap in `index.html` paints the right theme before the
 * school settings load — same key there, rename both together.
 */
export type ThemePolicy = { default: ThemeMode; allowSwitch: boolean };
export const THEME_POLICY_STORAGE_KEY = "school.theme-policy";
// light by default (owner 2026-10-26): a school that chose nothing opens light; «as the device» is an explicit pick
export const DEFAULT_THEME_POLICY: ThemePolicy = { default: "light", allowSwitch: true };

export function readThemePolicy(): ThemePolicy {
  try {
    const value = JSON.parse(window.localStorage.getItem(THEME_POLICY_STORAGE_KEY) ?? "null") as Partial<ThemePolicy> | null;
    return {
      default: isThemeMode(value?.default) ? value.default : DEFAULT_THEME_POLICY.default,
      allowSwitch: typeof value?.allowSwitch === "boolean" ? value.allowSwitch : DEFAULT_THEME_POLICY.allowSwitch,
    };
  } catch {
    return DEFAULT_THEME_POLICY;
  }
}

export function writeThemePolicy(policy: ThemePolicy) {
  try {
    window.localStorage.setItem(THEME_POLICY_STORAGE_KEY, JSON.stringify(policy));
  } catch {
    // not cached — the next load paints the previous policy until the school settings arrive
  }
}

/** The mode in force: the school's default when switching is off or the student never picked one. */
export const effectiveThemeMode = (stored: ThemeMode | null, policy: ThemePolicy): ThemeMode =>
  policy.allowSwitch ? (stored ?? policy.default) : policy.default;

export const readThemeMode = (): ThemeMode => effectiveThemeMode(readStoredThemeMode(), readThemePolicy());

export function writeThemeMode(mode: ThemeMode) {
  try {
    window.localStorage.setItem(THEME_STORAGE_KEY, mode);
  } catch {
    // not persisted — the choice still applies for this page
  }
}

const darkQuery = () =>
  typeof window === "undefined" ? null : (window.matchMedia?.("(prefers-color-scheme: dark)") ?? null);

export const getSystemTheme = (): ResolvedTheme => (darkQuery()?.matches ? "dark" : "light");

export const resolveTheme = (mode: ThemeMode): ResolvedTheme => (mode === "system" ? getSystemTheme() : mode);

/** Calls back when the OS appearance flips; returns the unsubscribe. */
export function subscribeSystemTheme(onChange: () => void): () => void {
  const query = darkQuery();
  if (!query) return () => {};
  query.addEventListener("change", onChange);
  return () => query.removeEventListener("change", onChange);
}

/** Browser chrome colour (address bar, iOS status bar) — the page background of each theme. */
const THEME_COLOR: Record<ResolvedTheme, string> = { light: "#f5f6f8", dark: "#0c0e12" };

export function applyDocumentTheme(theme: ResolvedTheme) {
  const root = document.documentElement;
  root.dataset.theme = theme;
  root.style.colorScheme = theme;
  let meta = document.querySelector<HTMLMetaElement>('meta[name="theme-color"]');
  if (!meta) {
    meta = document.createElement("meta");
    meta.name = "theme-color";
    document.head.appendChild(meta);
  }
  meta.content = THEME_COLOR[theme];
}

export const readDocumentTheme = (): ResolvedTheme =>
  document.documentElement.dataset.theme === "dark" ? "dark" : "light";

/** Shaped for `useSyncExternalStore`: keep it module-level so its identity is stable. */
export function subscribeDocumentTheme(onChange: () => void): () => void {
  if (typeof MutationObserver === "undefined") return () => {};
  const observer = new MutationObserver(onChange);
  observer.observe(document.documentElement, { attributes: true, attributeFilter: ["data-theme"] });
  return () => observer.disconnect();
}
